import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    // Close on Escape or tab-out. Listen on the parent <details> so Escape works from the summary too.
    this.closest('details')?.addEventListener('keydown', this.#handleKeyDown);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.closest('details')?.removeEventListener('keydown', this.#handleKeyDown);
    this.#mutationObserver.disconnect();
  }

  /** Visible, focusable elements inside the panel, in DOM order. */
  get #focusableElements() {
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll(selector)).filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null
      )
    );
  }

  /**
   * Closes the parent <details> panel.
   * @param {boolean} [focusSummary=true] - Whether to return focus to the summary.
   */
  #close(focusSummary = true) {
    const details = this.closest('details');
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    details.open = false;
    details.removeAttribute('open');

    if (focusSummary) {
      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus();
    }
  }

  /** Moves focus to the first focusable element after the summary, so tabbing out continues naturally. */
  #focusAfterSummary() {
    const details = this.closest('details');
    const summary = details?.querySelector('summary');
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex]:not([tabindex="-1"])';

    const focusable = Array.from(document.querySelectorAll(selector)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null && !details?.contains(el)
    );

    if (!(summary instanceof HTMLElement)) {
      /** @type {HTMLElement | undefined} */ (focusable[0])?.focus();
      return;
    }

    const next = focusable.find(
      (el) => summary.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );

    /** @type {HTMLElement} */ (next ?? summary).focus();
  }

  /**
   * Handles Escape and tab-out to close the panel.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close(true);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#focusableElements;
    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (!event.shiftKey && lastElement && document.activeElement === lastElement) {
      // Tab forward past the last element: close and continue to the next control.
      event.preventDefault();
      this.#close(false);
      this.#focusAfterSummary();
    } else if (event.shiftKey && firstElement && document.activeElement === firstElement) {
      // Shift+Tab on the first element: close and return focus to the summary.
      event.preventDefault();
      this.#close(true);
    }
  };
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
